import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ApolloError, useLazyQuery, useMutation } from '@apollo/client';
import { notify } from '../../helpers/toastify';

import { GET_CLIENT_ACTIVITY, SIGN_CLIENT_ACTIVITY } from '@bloomays-lib/adapter.api-bloomer';

import { FallbackError, WithErrorBoundary, LoaderSkeleton } from '@bloomays-lib/ui.shared';
import path from 'path';
import { useTranslation } from 'react-i18next';
import { convertDateToStringFormat } from '@bloomays-lib/utils.shared';
import { getApolloErrorCode, getAppoloErrorExtensions } from '../../helpers/error';
import { AirtableActivity, IActivity, IMission } from '@bloomays-lib/types.shared';
import CRAValidation, { ClientSignatureData } from '../organisms/CRAValidation';
import { ActivitySummary } from '../../types/components';
import { activityToActivitySummary } from '../../helpers/CRA';
import { styled } from '@mui/material/styles';

const EmbedCRAInviteClient = () => {
  const [activitySummary, setActivitySummary] = useState<ActivitySummary>();
  const { id: signingDocumentId } = useParams<{ id: string }>();

  const navigate = useNavigate();
  const { t } = useTranslation(['random', 'notify']);
  const [activityNotFound, setActivityNotFound] = useState<boolean>(false);

  const [signClientActivity] = useMutation<
    { signClientActivity: IActivity },
    { signingDocumentID: string; signerFullname?: string }
  >(SIGN_CLIENT_ACTIVITY);

  const [getClientActivity, { loading: loadingClientActivity, error: errorClientActivity }] = useLazyQuery<
    { getClientActivity: IActivity },
    { signingDocumentID: string }
  >(GET_CLIENT_ACTIVITY);

  const handleError = useCallback(
    (error: ApolloError) => {
      const code = getApolloErrorCode(error);
      const extensions = getAppoloErrorExtensions(error);
      const activity = (extensions?.meta as { activity: AirtableActivity })?.activity;
      switch (code) {
        case 'TOKEN_EXPIRED':
          notify('warning', t('expiredLink', { ns: 'notify' }), undefined, {
            autoClose: false,
            hideProgressBar: true,
          });
          break;
        case 'ALREADY_SIGNED':
          notify('info', t('craAlreadySigned', { ns: 'notify' }));
          if (activity && activity.urlCraS3) {
            navigate(
              `/files/activity/${convertDateToStringFormat(activity.month, 'yyyy-MM-dd')}/${
                activity.recordId
              }/cra/${path.basename(activity.urlCraS3)}`,
            );
          }
          break;
        case 'OBJECT_NOT_FOUND':
          notify('warning', t('invalidInviteLink', { ns: 'notify' }));
          setActivityNotFound(true);
          break;
        default:
          notify('error', t('linkError', { ns: 'notify' }), error);
      }
    },
    [t, navigate],
  );

  useEffect(() => {
    (async () => {
      if (signingDocumentId) {
        try {
          const res = await getClientActivity({
            variables: {
              signingDocumentID: signingDocumentId,
            },
          });
          if (res?.data?.getClientActivity) {
            setActivitySummary(
              activityToActivitySummary(res.data.getClientActivity, res.data.getClientActivity.mission as IMission),
            );
          }
        } catch (e) {
          console.warn('Handled error in useEffect to get embed cra invite', e);
          return;
        }
      }
    })();
  }, [getClientActivity, signingDocumentId]);

  useEffect(() => {
    if (errorClientActivity) {
      handleError(errorClientActivity);
    }
    return;
  }, [errorClientActivity, handleError]);

  if (activityNotFound) {
    return <FallbackError hideRedirection={true} errorText={t('invalidDoc', { ns: 'notify' })} />;
  }
  if (loadingClientActivity || !activitySummary)
    return <LoaderSkeleton label={t('loadingText', { ns: 'random' })} height={2000} width={'90%'} />;

  return (
    <Container>
      <CRAValidation
        activitySummary={activitySummary}
        onClientSign={async (signatureData: ClientSignatureData) => {
          if (!signingDocumentId) {
            throw new Error('Signing document ID is required');
          }
          try {
            const res = await signClientActivity({
              variables: {
                signingDocumentID: signingDocumentId,
                signerFullname: signatureData.signerName,
              },
            });
            if (!res.data?.signClientActivity) {
              return;
            }
            setActivitySummary(
              activityToActivitySummary(res.data.signClientActivity, res.data.signClientActivity.mission as IMission),
            );
            notify('success', t('signSuccess', { ns: 'notify' }));
            return;
          } catch (error) {
            notify('error', t('signError', { ns: 'notify' }), error, {
              autoClose: false,
              hideProgressBar: true,
            });
          }
        }}
      />
    </Container>
  );
};

const Container = styled('div')(({ theme }) => ({
  margin: 'auto',
  width: '90%',
}));

export default WithErrorBoundary(EmbedCRAInviteClient);
